import * as React from "react";
import { HelpCircle, MessageSquare, Plus, RefreshCw, Send } from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { AIDraftButton } from "@eduviaos/capability-ai-ui";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { useAIDraftGenerator, type HelpdeskReplyDraft } from "@/lib/aiDraft";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { useAuth } from "@/auth/AuthProvider";
import {
  TICKET_PRIORITY_LABELS,
  TICKET_STATUS_LABELS,
  TICKET_STATUS_PILL,
  useCreateHelpdeskMessage,
  useCreateHelpdeskTicket,
  useHelpdeskMessages,
  useHelpdeskRealtime,
  useHelpdeskTickets,
  useMarkHelpdeskRead,
  useUpdateHelpdeskTicket,
  openHelpdeskFile,
  uploadHelpdeskFile,
  type HelpdeskTicket,
  type TicketChannel,
  type TicketPriority,
  type TicketStatus,
} from "@/lib/helpdesk/api";

function describeReplyDraft(draft: HelpdeskReplyDraft) {
  return draft.body ? [{ label: "Reply", value: draft.body }] : [];
}

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(iso),
  );
}

export function HelpdeskPage() {
  const { user } = useAuth();
  const { vertical } = useEnabledModules();
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "helpdesk",
    defaultPageSize: 10,
  });
  const [statusFilter, setStatusFilter] = React.useState<TicketStatus | "all">("all");
  const [channelFilter, setChannelFilter] = React.useState<TicketChannel | "all">("all");
  const [activeTicket, setActiveTicket] = React.useState<HelpdeskTicket | null>(null);
  const [replyBody, setReplyBody] = React.useState("");
  const [replyFiles, setReplyFiles] = React.useState<File[]>([]);
  const generateReplyDraft = useAIDraftGenerator("helpdesk_reply", {
    ticket_id: activeTicket?.id,
  });
  const [createOpen, setCreateOpen] = React.useState(false);
  const [subject, setSubject] = React.useState("");
  const [body, setBody] = React.useState("");
  const [requesterName, setRequesterName] = React.useState("");
  const [requesterEmail, setRequesterEmail] = React.useState("");
  const [priority, setPriority] = React.useState<TicketPriority>("normal");
  const [channel, setChannel] = React.useState<TicketChannel>("learner_centre");

  const ticketsQuery = useHelpdeskTickets({
    status: statusFilter === "all" ? undefined : statusFilter,
    channel: channelFilter === "all" ? undefined : channelFilter,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });
  const createMutation = useCreateHelpdeskTicket();
  const updateMutation = useUpdateHelpdeskTicket();
  const messagesQuery = useHelpdeskMessages(activeTicket?.id ?? null);
  useHelpdeskRealtime(activeTicket?.id ?? null);
  const createMessage = useCreateHelpdeskMessage();
  const { mutate: markTicketRead } = useMarkHelpdeskRead();

  const total = ticketsQuery.data?.total ?? 0;
  const items = React.useMemo(() => ticketsQuery.data?.items ?? [], [ticketsQuery.data?.items]);

  const resetForm = () => {
    setSubject("");
    setBody("");
    setRequesterName("");
    setRequesterEmail("");
    setPriority("normal");
    setChannel("learner_centre");
  };

  React.useEffect(() => {
    if (!activeTicket || activeTicket.responder_unread_count === 0) return;
    markTicketRead(activeTicket.id);
  }, [activeTicket, markTicketRead]);

  const handleCreate = async () => {
    if (!subject.trim() || !body.trim() || !requesterName.trim()) {
      toast.error("Subject, description, and requester name are required.");
      return;
    }
    try {
      await createMutation.mutateAsync({
        subject: subject.trim(),
        body: body.trim(),
        requester_name: requesterName.trim(),
        requester_email: requesterEmail.trim() || null,
        priority,
        channel,
      });
      toast.success("Ticket created.");
      setCreateOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not create ticket.");
    }
  };

  const handleReply = async () => {
    if (!activeTicket || (!replyBody.trim() && replyFiles.length === 0)) return;
    const content = replyBody.trim();
    const chosen = replyFiles;
    setReplyBody("");
    setReplyFiles([]);
    try {
      const attachments = [];
      for (const file of chosen) {
        attachments.push(await uploadHelpdeskFile(activeTicket.id, file));
      }
      await createMessage.mutateAsync({
        ticketId: activeTicket.id,
        body: content,
        clientMessageId: crypto.randomUUID(),
        attachments,
      });
    } catch (err) {
      setReplyBody(content);
      setReplyFiles(chosen);
      toast.error(err instanceof Error ? err.message : "Could not send reply.");
    }
  };

  const handleStatusChange = async (id: string, status: TicketStatus) => {
    try {
      await updateMutation.mutateAsync({ id, status });
      toast.success(`Ticket marked ${TICKET_STATUS_LABELS[status].toLowerCase()}.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update ticket.");
    }
  };

  const assignToMe = async (id: string) => {
    if (!user) return;
    try {
      await updateMutation.mutateAsync({ id, assigned_to: user.id });
      toast.success("Ticket assigned to you.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not assign ticket.");
    }
  };

  const unassign = async (id: string) => {
    try {
      await updateMutation.mutateAsync({ id, assigned_to: null });
      toast.success("Ticket returned to the unassigned queue.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not unassign ticket.");
    }
  };

  return (
    <div className="mx-auto max-w-6xl py-8">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Helpdesk</PageHeaderHeading>
            <PageHeaderDescription>
              {vertical === "study_centre"
                ? "Requests from learners, and requests your centre sends to EduViaOS."
                : "Track learner and parent queries from intake through resolution."}
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void ticketsQuery.refetch()}
              disabled={ticketsQuery.isFetching}
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", ticketsQuery.isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden="true" className="size-4" />
              New ticket
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <div className="mt-6">
        <DataTable>
          <DataTableToolbar>
            <HelpCircle aria-hidden="true" className="text-muted-foreground size-4" />
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v as TicketStatus | "all");
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {(Object.keys(TICKET_STATUS_LABELS) as TicketStatus[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    {TICKET_STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={channelFilter}
              onValueChange={(value) => {
                setChannelFilter(value as TicketChannel | "all");
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[190px]">
                <SelectValue placeholder="Inbox" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All inboxes</SelectItem>
                <SelectItem value="learner_centre">Learners → centre</SelectItem>
                <SelectItem value="centre_platform">Centre → EduViaOS</SelectItem>
              </SelectContent>
            </Select>
            <DataTableToolbarSpacer />
          </DataTableToolbar>

          {ticketsQuery.isLoading ? (
            <DataTableSkeleton columns={6} rows={8} />
          ) : ticketsQuery.isError ? (
            <div className="border-border/60 bg-muted/30 text-muted-foreground rounded-md border p-6 text-sm">
              Could not load tickets.{" "}
              <button
                type="button"
                className="underline"
                onClick={() => void ticketsQuery.refetch()}
              >
                Retry
              </button>
            </div>
          ) : items.length === 0 ? (
            <DataTableEmpty
              title="No tickets yet"
              description={
                vertical === "study_centre"
                  ? "Create a ticket when a learner reaches out — track it here until it's resolved."
                  : "Create a ticket when a learner or parent reaches out — track it here until it's resolved."
              }
              action={
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  New ticket
                </Button>
              }
            />
          ) : (
            <>
              <div className="space-y-3 p-3 sm:hidden">
                {items.map((row) => (
                  <article key={row.id} className="border-border/60 rounded-xl border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        className="text-left font-medium hover:underline"
                        onClick={() => setActiveTicket(row)}
                      >
                        {row.subject}
                      </button>
                      <StatusPill
                        status={TICKET_STATUS_PILL[row.status]}
                        label={TICKET_STATUS_LABELS[row.status]}
                      />
                    </div>
                    <p className="text-muted-foreground mt-2 text-xs">
                      {row.requester_name} ·{" "}
                      {row.channel === "learner_centre" ? "Learner → centre" : "Centre → EduViaOS"}
                    </p>
                    <div className="text-muted-foreground mt-3 flex justify-between gap-3 text-xs">
                      <span>
                        {row.message_count} message{row.message_count === 1 ? "" : "s"}
                        {row.responder_unread_count > 0
                          ? ` · ${row.responder_unread_count} new`
                          : ""}
                      </span>
                      <time dateTime={row.created_at}>{formatWhen(row.created_at)}</time>
                    </div>
                  </article>
                ))}
              </div>
              <div className="hidden sm:block">
                <DataTableScroll>
                  <Table>
                    <TableCaption className="sr-only">Helpdesk tickets</TableCaption>
                    <TableHeader className="bg-background sticky top-0 z-10">
                      <TableRow>
                        <TableHead className="bg-background sticky left-0 z-20 shadow-[inset_-1px_0_0_hsl(var(--border))]">
                          Subject
                        </TableHead>
                        <TableHead>Requester</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Opened</TableHead>
                        <TableHead className="w-[1%]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody className="divide-border/60 divide-y">
                      {items.map((row) => (
                        <TableRow key={row.id} className="hover:bg-muted/40">
                          <TableCell className="bg-background sticky left-0 z-10 max-w-[240px] truncate font-medium shadow-[inset_-1px_0_0_hsl(var(--border))]">
                            <button
                              type="button"
                              className="hover:underline"
                              onClick={() => setActiveTicket(row)}
                            >
                              {row.subject}
                            </button>
                            <span className="text-muted-foreground mt-0.5 block text-[10px]">
                              {row.channel === "learner_centre"
                                ? "Learner → centre"
                                : "Centre → EduViaOS"}{" "}
                              · {row.message_count} message{row.message_count === 1 ? "" : "s"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="grid">
                              <span>{row.requester_name}</span>
                              {row.requester_email ? (
                                <span className="text-muted-foreground truncate text-xs">
                                  {row.requester_email}
                                </span>
                              ) : null}
                              {row.assigned_to ? (
                                <span className="text-muted-foreground truncate text-[10px]">
                                  Assigned
                                  {row.assigned_to === user?.id ? " to you" : " to a teammate"}
                                </span>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell>{TICKET_PRIORITY_LABELS[row.priority]}</TableCell>
                          <TableCell>
                            <StatusPill
                              status={TICKET_STATUS_PILL[row.status]}
                              label={TICKET_STATUS_LABELS[row.status]}
                            />
                          </TableCell>
                          <TableCell className="text-right tabular-nums" title={row.created_at}>
                            {formatWhen(row.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="Ticket actions">
                                  …
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {row.assigned_to !== user?.id ? (
                                  <DropdownMenuItem
                                    disabled={updateMutation.isPending}
                                    onClick={() => void assignToMe(row.id)}
                                  >
                                    Assign to me
                                  </DropdownMenuItem>
                                ) : null}
                                {row.assigned_to ? (
                                  <DropdownMenuItem
                                    disabled={updateMutation.isPending}
                                    onClick={() => void unassign(row.id)}
                                  >
                                    Unassign
                                  </DropdownMenuItem>
                                ) : null}
                                {(Object.keys(TICKET_STATUS_LABELS) as TicketStatus[]).map((s) => (
                                  <DropdownMenuItem
                                    key={s}
                                    disabled={row.status === s || updateMutation.isPending}
                                    onClick={() => void handleStatusChange(row.id, s)}
                                  >
                                    Mark {TICKET_STATUS_LABELS[s].toLowerCase()}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </DataTableScroll>
              </div>
              <DataTableFooter>
                <span className="text-muted-foreground text-xs">0 of {total} row(s) selected.</span>
                <DataTablePagination
                  page={page}
                  pageSize={pageSize}
                  totalRows={total}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                />
              </DataTableFooter>
            </>
          )}
        </DataTable>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New helpdesk ticket</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="ticket-subject">Subject</Label>
              <Input
                id="ticket-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ticket-body">Description</Label>
              <Textarea
                id="ticket-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
                maxLength={4000}
              />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="ticket-requester">Requester name</Label>
                <Input
                  id="ticket-requester"
                  value={requesterName}
                  onChange={(e) => setRequesterName(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ticket-email">Requester email (optional)</Label>
                <Input
                  id="ticket-email"
                  type="email"
                  value={requesterEmail}
                  onChange={(e) => setRequesterEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TicketPriority)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(TICKET_PRIORITY_LABELS) as TicketPriority[]).map((p) => (
                    <SelectItem key={p} value={p}>
                      {TICKET_PRIORITY_LABELS[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Inbox</Label>
              <Select value={channel} onValueChange={(v) => setChannel(v as TicketChannel)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="learner_centre">
                    {vertical === "study_centre"
                      ? "Learner → centre"
                      : "Learner or parent → centre"}
                  </SelectItem>
                  <SelectItem value="centre_platform">Centre → EduViaOS support</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreate()} disabled={createMutation.isPending}>
              Create ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet
        open={activeTicket !== null}
        onOpenChange={(open) => {
          if (!open) {
            setActiveTicket(null);
            setReplyBody("");
          }
        }}
      >
        <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-2xl">
          <SheetHeader className="border-border/60 border-b p-6">
            <SheetTitle>{activeTicket?.subject ?? "Support thread"}</SheetTitle>
            <SheetDescription>
              {activeTicket?.channel === "centre_platform"
                ? "Conversation between your centre and EduViaOS support."
                : "Conversation between the learner and your centre team."}
            </SheetDescription>
            {activeTicket ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="mt-3 w-fit">
                    Manage request
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {activeTicket.assigned_to !== user?.id ? (
                    <DropdownMenuItem
                      disabled={!user || updateMutation.isPending}
                      onSelect={() => {
                        if (!user) return;
                        updateMutation.mutate(
                          { id: activeTicket.id, assigned_to: user.id },
                          { onSuccess: (ticket) => setActiveTicket(ticket) },
                        );
                      }}
                    >
                      Assign to me
                    </DropdownMenuItem>
                  ) : null}
                  {activeTicket.assigned_to ? (
                    <DropdownMenuItem
                      disabled={updateMutation.isPending}
                      onSelect={() =>
                        updateMutation.mutate(
                          { id: activeTicket.id, assigned_to: null },
                          { onSuccess: (ticket) => setActiveTicket(ticket) },
                        )
                      }
                    >
                      Unassign
                    </DropdownMenuItem>
                  ) : null}
                  {(Object.keys(TICKET_STATUS_LABELS) as TicketStatus[]).map((status) => (
                    <DropdownMenuItem
                      key={status}
                      disabled={activeTicket.status === status || updateMutation.isPending}
                      onSelect={() =>
                        updateMutation.mutate(
                          { id: activeTicket.id, status },
                          { onSuccess: (ticket) => setActiveTicket(ticket) },
                        )
                      }
                    >
                      Mark {TICKET_STATUS_LABELS[status].toLowerCase()}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </SheetHeader>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
            {messagesQuery.isLoading ? (
              <div className="space-y-3" aria-label="Loading messages">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className="bg-muted h-20 animate-pulse rounded-xl"
                    aria-hidden="true"
                  />
                ))}
              </div>
            ) : messagesQuery.isError ? (
              <div className="border-destructive/30 bg-destructive/5 rounded-lg border p-4 text-sm">
                Could not load this conversation.
                <Button variant="link" size="sm" onClick={() => void messagesQuery.refetch()}>
                  Retry
                </Button>
              </div>
            ) : (
              (messagesQuery.data?.items ?? []).map((message) => (
                <article
                  key={message.id}
                  className="border-border/60 bg-card rounded-xl border p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium">{message.author_name}</span>
                    <time
                      className="text-muted-foreground text-[10px] tabular-nums"
                      dateTime={message.created_at}
                    >
                      {formatWhen(message.created_at)}
                    </time>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{message.body}</p>
                  {(message.attachments ?? []).length > 0 ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {message.attachments.map((file) => (
                        <Button
                          key={file.object_path}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            void openHelpdeskFile(message.ticket_id, file.object_path).catch(() =>
                              toast.error("Could not open that file."),
                            )
                          }
                        >
                          {file.filename}
                        </Button>
                      ))}
                    </div>
                  ) : null}
                  <div className="text-muted-foreground mt-2 flex items-center gap-1 text-[10px]">
                    <MessageSquare aria-hidden="true" className="size-3" />
                    {message.delivery_status === "sending"
                      ? "Sending…"
                      : message.moderation_status === "visible"
                        ? "Delivered"
                        : "Under review"}
                  </div>
                </article>
              ))
            )}
          </div>

          <div className="border-border/60 space-y-3 border-t p-6">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="helpdesk-reply">Reply</Label>
              <AIDraftButton<HelpdeskReplyDraft>
                label="Draft reply"
                title="Draft a reply"
                description="The draft reads this conversation. You can edit it before sending."
                placeholder="e.g. Apologise for the delay and confirm the refund is processed by Friday"
                generate={generateReplyDraft}
                describe={describeReplyDraft}
                onApply={(draft) => setReplyBody((draft.body ?? "").slice(0, 4000))}
                disabled={!activeTicket || createMessage.isPending}
                data-testid="helpdesk-ai-draft"
              />
            </div>
            <Textarea
              id="helpdesk-reply"
              value={replyBody}
              onChange={(event) => setReplyBody(event.target.value)}
              placeholder="Write a helpful response…"
              rows={3}
              maxLength={4000}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor="helpdesk-files" className="cursor-pointer text-sm underline">
                Add image or document
              </Label>
              <input
                id="helpdesk-files"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,text/plain,.doc,.docx,.xlsx"
                multiple
                className="sr-only"
                onChange={(event) => {
                  const picked = Array.from(event.target.files ?? []);
                  setReplyFiles((current) => [...current, ...picked].slice(0, 4));
                  event.target.value = "";
                }}
              />
              {replyFiles.map((file) => (
                <span key={`${file.name}-${file.size}`} className="text-muted-foreground text-xs">
                  {file.name}
                </span>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-[10px] tabular-nums">
                {replyBody.length}/4000
              </span>
              <Button
                onClick={() => void handleReply()}
                disabled={(!replyBody.trim() && replyFiles.length === 0) || createMessage.isPending}
              >
                <Send aria-hidden="true" className="size-4" />
                {createMessage.isPending ? "Sending…" : "Send reply"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
